import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Zap, Flame, Trophy, Loader2, Sparkles, CheckCircle2,
  Play, Pause, RotateCcw, Star,
} from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

type Challenge = {
  id: string;
  challenge_date: string;
  trait: string;
  title: string;
  prompt: string;
  challenge_type: string;
  duration_seconds: number;
  completed: boolean;
  response: string | null;
  xp_awarded: number;
  completed_at: string | null;
};

type XP = {
  total_xp: number;
  current_streak: number;
  longest_streak: number;
  last_completed_date: string | null;
};

const TYPE_META: Record<string, { label: string; color: string }> = {
  reflect: { label: "Reflect", color: "bg-blue-500/10 text-blue-500" },
  act: { label: "Act", color: "bg-emerald-500/10 text-emerald-500" },
  create: { label: "Create", color: "bg-purple-500/10 text-purple-500" },
  observe: { label: "Observe", color: "bg-amber-500/10 text-amber-500" },
};

const levelFromXp = (xp: number) => {
  const level = Math.floor(Math.sqrt(xp / 50)) + 1;
  const currentLevelXp = (level - 1) ** 2 * 50;
  const nextLevelXp = level ** 2 * 50;
  const progress = ((xp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100;
  return { level, progress: Math.max(0, Math.min(100, progress)), nextLevelXp, currentLevelXp };
};

export default function Challenges() {
  const { user } = useAuth();
  const [today, setToday] = useState<Challenge | null>(null);
  const [history, setHistory] = useState<Challenge[]>([]);
  const [xp, setXp] = useState<XP>({ total_xp: 0, current_streak: 0, longest_streak: 0, last_completed_date: null });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [response, setResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Timer
  const [timerSeconds, setTimerSeconds] = useState(30);
  const [timerActive, setTimerActive] = useState(false);

  useEffect(() => {
    if (!timerActive) return;
    if (timerSeconds <= 0) {
      setTimerActive(false);
      return;
    }
    const id = setTimeout(() => setTimerSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [timerActive, timerSeconds]);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const todayStr = new Date().toISOString().slice(0, 10);
    const [c, x] = await Promise.all([
      supabase.from("daily_challenges").select("*").eq("user_id", user.id).order("challenge_date", { ascending: false }).limit(30),
      supabase.from("user_xp").select("*").eq("user_id", user.id).maybeSingle(),
    ]);
    const all = (c.data as Challenge[]) ?? [];
    const t = all.find((x) => x.challenge_date === todayStr) ?? null;
    setToday(t);
    setHistory(all.filter((x) => x.challenge_date !== todayStr));
    if (t) setTimerSeconds(t.duration_seconds);
    if (x.data) setXp(x.data as XP);
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  const generateToday = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-daily-challenge", { body: {} });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setToday(data.challenge);
      setTimerSeconds(data.challenge.duration_seconds);
      toast.success("Today's challenge is ready");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const completeChallenge = async () => {
    if (!today || !user) return;
    setSubmitting(true);
    try {
      const xpAwarded = 10;
      const todayStr = new Date().toISOString().slice(0, 10);
      const yesterdayStr = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      const continued = xp.last_completed_date === yesterdayStr;
      const isFirst = !xp.last_completed_date;
      const newStreak = isFirst || !continued ? 1 : xp.current_streak + 1;
      const streakBonus = newStreak >= 7 ? 15 : newStreak >= 3 ? 5 : 0;
      const total = xpAwarded + streakBonus;

      const { error: cErr } = await supabase
        .from("daily_challenges")
        .update({
          completed: true,
          response: response.trim() || null,
          xp_awarded: total,
          completed_at: new Date().toISOString(),
        })
        .eq("id", today.id);
      if (cErr) throw cErr;

      const newTotalXp = xp.total_xp + total;
      const newLongest = Math.max(xp.longest_streak, newStreak);
      const { error: xErr } = await supabase
        .from("user_xp")
        .upsert({
          user_id: user.id,
          total_xp: newTotalXp,
          current_streak: newStreak,
          longest_streak: newLongest,
          last_completed_date: todayStr,
        }, { onConflict: "user_id" });
      if (xErr) throw xErr;

      setXp({ total_xp: newTotalXp, current_streak: newStreak, longest_streak: newLongest, last_completed_date: todayStr });
      setToday({ ...today, completed: true, response: response.trim() || null, xp_awarded: total, completed_at: new Date().toISOString() });
      setResponse("");
      toast.success(`+${total} XP${streakBonus ? ` (streak bonus!)` : ""}`);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const { level, progress, nextLevelXp, currentLevelXp } = levelFromXp(xp.total_xp);
  const typeMeta = today ? (TYPE_META[today.challenge_type] || TYPE_META.reflect) : TYPE_META.reflect;
  const mins = Math.floor(timerSeconds / 60);
  const secs = timerSeconds % 60;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="gap-2">
              <Link to="/dashboard"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
            </Button>
            <WeKitLogo size="sm" />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">Daily Micro-Challenges</h1>
          <p className="text-muted-foreground mt-2">30 seconds. Real growth. Every day.</p>
        </div>

        {/* Stats */}
        <section className="grid grid-cols-3 gap-3 sm:gap-4">
          <Card className="border-border/50">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Flame className="h-3.5 w-3.5" /> Streak
              </div>
              <div className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                {xp.current_streak} <span className="text-sm font-normal text-muted-foreground">days</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Best: {xp.longest_streak}</div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Zap className="h-3.5 w-3.5" /> Total XP
              </div>
              <div className="font-display text-2xl sm:text-3xl font-bold text-foreground">{xp.total_xp}</div>
              <div className="text-xs text-muted-foreground mt-1">{nextLevelXp - xp.total_xp} to next level</div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Trophy className="h-3.5 w-3.5" /> Level
              </div>
              <div className="font-display text-2xl sm:text-3xl font-bold text-foreground">{level}</div>
              <Progress value={progress} className="h-1.5 mt-2" />
            </CardContent>
          </Card>
        </section>

        {/* Today */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : !today ? (
          <Card className="border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
            <CardContent className="p-8 sm:p-12 text-center">
              <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-foreground">Ready for today's challenge?</h3>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                YOMA picks a 30-second micro-challenge tied to your strongest traits.
              </p>
              <Button onClick={generateToday} disabled={generating} className="mt-6 rounded-full gap-2">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Generate today's challenge
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5 overflow-hidden">
            <CardContent className="p-6 sm:p-8 space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={typeMeta.color + " hover:" + typeMeta.color}>{typeMeta.label}</Badge>
                <Badge variant="outline" className="text-xs capitalize">{today.trait}</Badge>
                {today.completed && (
                  <Badge className="bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Done · +{today.xp_awarded} XP
                  </Badge>
                )}
              </div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">{today.title}</h2>
                <p className="text-foreground/80 mt-2 text-base sm:text-lg leading-relaxed">{today.prompt}</p>
              </div>

              {!today.completed && (
                <>
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-background/50 border border-border/50">
                    <div className="font-display text-3xl font-bold text-primary tabular-nums">
                      {mins}:{secs.toString().padStart(2, "0")}
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <Button size="sm" variant="outline" className="rounded-full gap-1.5"
                        onClick={() => setTimerActive(!timerActive)}>
                        {timerActive ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                        {timerActive ? "Pause" : "Start"}
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full"
                        onClick={() => { setTimerActive(false); setTimerSeconds(today.duration_seconds); }}>
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  <Textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Optional: jot a quick note about what you did or noticed..."
                    rows={3}
                  />
                  <Button onClick={completeChallenge} disabled={submitting}
                    className="w-full rounded-full gap-2 bg-gradient-to-r from-primary to-accent">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Mark complete
                  </Button>
                </>
              )}

              {today.completed && today.response && (
                <div className="p-4 rounded-xl bg-background/50 border border-border/50">
                  <div className="text-xs text-muted-foreground mb-1">Your note</div>
                  <p className="text-sm text-foreground">{today.response}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* History */}
        {history.length > 0 && (
          <section className="space-y-3">
            <h2 className="font-display text-xl font-semibold text-foreground">Recent challenges</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {history.slice(0, 10).map((c) => {
                const meta = TYPE_META[c.challenge_type] || TYPE_META.reflect;
                return (
                  <Card key={c.id} className="border-border/50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={meta.color + " hover:" + meta.color + " text-xs"}>{meta.label}</Badge>
                          <Badge variant="outline" className="text-xs capitalize">{c.trait}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(c.challenge_date), "MMM d")}
                        </div>
                      </div>
                      <h4 className="font-medium text-foreground text-sm">{c.title}</h4>
                      <div className="flex items-center justify-between text-xs">
                        {c.completed ? (
                          <span className="text-emerald-500 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Done
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Skipped</span>
                        )}
                        {c.xp_awarded > 0 && (
                          <span className="text-primary flex items-center gap-1">
                            <Star className="h-3 w-3" /> +{c.xp_awarded} XP
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
